/**
 * @Author: Zero
 * @Date: 2026-07-08 17:11:41
 * @Explain: 自动处理浏览器兼容性、网络状态监听以及不支持 API 时的测速降级逻辑，并根据当前网络情况返回对应的配置对象
 */

import { useCallback, useEffect, useState } from 'react';

// 定义网络类型的枚举
export enum NetworkTypeEnum {
  FourG = '4g',
  ThreeG = '3g',
  TwoG = '2g',
  SlowTwoG = 'slow-2g',
  Offline = 'offline',
  Unknown = 'unknown',
}
// 定义 Hook 返回值的接口
export interface NetworkStatus {
  // 网络类型
  networkType: NetworkTypeEnum;
  // 是否在线
  isOnline: boolean;
}

/**
 * 自定义 Hook：获取网络状态及对应配置
 * @returns 网络状态
 */
export function useNetworkStatus(): NetworkStatus {
  // 测速用的图片URL
  const speedTestUrl: string = '@/assets/images/testerManage/light-right.png';
  // 网络类型
  const [networkType, setNetworkType] = useState<NetworkTypeEnum>(
    NetworkTypeEnum.ThreeG,
  );
  // 是否在线
  const [isOnline, setIsOnline] = useState<boolean>(navigator?.onLine);

  // 测速降级函数
  const estimateNetworkType = useCallback(async () => {
    const testUrl = `${speedTestUrl}?t=${Date.now()}`; // 加时间戳防止缓存
    const startTime = Date.now();

    try {
      const response = await fetch(testUrl, { cache: 'no-store' });
      const blob = await response.blob();
      const endTime = Date.now();

      const duration = (endTime - startTime) / 1000; // 秒
      if (duration === 0) return NetworkTypeEnum.FourG; // 耗时极短，视为好网络

      const sizeInKB = blob.size / 1024;
      const speedKBps = sizeInKB / duration; // KB/s

      // 根据测速结果简单映射到网络类型（阈值可根据实际业务调整）
      if (speedKBps > 300) return NetworkTypeEnum.FourG;
      if (speedKBps > 50) return NetworkTypeEnum.ThreeG;
      if (speedKBps > 10) return NetworkTypeEnum.TwoG;
      return NetworkTypeEnum.SlowTwoG;
    } catch {
      // 测速失败，降级为 unknown
      return NetworkTypeEnum.Unknown;
    }
  }, [speedTestUrl]);

  // 更新网络状态的函数
  const updateNetworkStatus = useCallback(async () => {
    if (!navigator.onLine) {
      setNetworkType(NetworkTypeEnum.Offline);
      setIsOnline(false);
      return;
    }

    setIsOnline(true);

    // 优先使用 navigator.connection
    const connection =
      (navigator as any)?.connection ||
      (navigator as any)?.mozConnection ||
      (navigator as any)?.webkitConnection;

    if (
      connection &&
      connection?.effectiveType &&
      connection?.effectiveType !== NetworkTypeEnum.Unknown
    ) {
      setNetworkType(connection?.effectiveType as NetworkTypeEnum);
    } else {
      // 不支持 API 或返回 unknown (如 iOS Safari)，触发测速降级
      const estimatedType = await estimateNetworkType();
      setNetworkType(estimatedType);
    }
  }, [estimateNetworkType]);

  useEffect(() => {
    // 组件挂载时初始化获取一次
    updateNetworkStatus();

    // 监听在线/离线事件
    const handleOnline = () => updateNetworkStatus();
    const handleOffline = () => updateNetworkStatus();
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // 监听网络变化事件 (Chromium 系)
    const connection =
      (navigator as any)?.connection ||
      (navigator as any)?.mozConnection ||
      (navigator as any)?.webkitConnection;

    const handleConnectionChange = () => updateNetworkStatus();

    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    // 清理监听器
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [updateNetworkStatus]);

  return {
    networkType,
    isOnline,
  };
}
